import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getPackagingOptions, createPackagingOption, getProductPackaging,
  setProductPackaging, packagingMapFromList, stockModeMapFromList, eanMapFromList, codeMapFromList,
} from "@/lib/api";
import { useProductTypes } from "@/context/ProductTypesContext";
import { useT } from "@/i18n/I18nContext";
import { Boxes, Plus, Check, Scale, Package } from "lucide-react";

export default function ProductPackaging() {
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const [packagings, setPackagings] = useState([]);
  const [assign, setAssign] = useState({}); // product -> [labels]
  const [modes, setModes] = useState({});   // product -> "bulk_kg" | "package"
  const [eans, setEans] = useState({});     // product -> {label: ean}
  const [codes, setCodes] = useState({});   // product -> {label: code}
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pk, pp] = await Promise.all([getPackagingOptions(), getProductPackaging()]);
      setPackagings(pk.filter((x) => x.active !== false));
      setAssign(packagingMapFromList(pp));
      setModes(stockModeMapFromList(pp));
      setEans(eanMapFromList(pp));
      setCodes(codeMapFromList(pp));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Global package labels excluding the bulk "kg" master (always available everywhere).
  const labels = useMemo(() => packagings.filter((p) => p.label !== "kg"), [packagings]);

  // Effective stock mode: explicit setting, else default to bulk kg when packages are assigned.
  const modeOf = (product) => modes[product] || ((assign[product] || []).length ? "bulk_kg" : "package");

  const toggle = async (product, label) => {
    const current = assign[product] || [];
    const next = current.includes(label) ? current.filter((l) => l !== label) : [...current, label];
    setAssign((m) => ({ ...m, [product]: next })); // optimistic
    try {
      await setProductPackaging(product, next, modeOf(product), eans[product], codes[product]);
    } catch (e) {
      setAssign((m) => ({ ...m, [product]: current })); // revert
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  const setMode = async (product, mode) => {
    const prev = modes[product];
    setModes((m) => ({ ...m, [product]: mode })); // optimistic
    try {
      await setProductPackaging(product, assign[product] || [], mode, eans[product], codes[product]);
    } catch (e) {
      setModes((m) => ({ ...m, [product]: prev }));
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  // Persist an EAN barcode for one (product, package label). Called on input blur.
  const saveEan = async (product, label, value) => {
    const code = (value || "").trim();
    const prev = eans[product] || {};
    if ((prev[label] || "") === code) return; // unchanged
    const next = { ...prev };
    if (code) next[label] = code; else delete next[label];
    setEans((m) => ({ ...m, [product]: next })); // optimistic
    try {
      await setProductPackaging(product, assign[product] || [], modeOf(product), next, codes[product]);
      toast.success(t("productPackaging.eanSaved"));
    } catch (e) {
      setEans((m) => ({ ...m, [product]: prev }));
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  // Persist a product code for one (product, package label). Called on input blur.
  const saveCode = async (product, label, value) => {
    const val = (value || "").trim();
    const prev = codes[product] || {};
    if ((prev[label] || "") === val) return; // unchanged
    const next = { ...prev };
    if (val) next[label] = val; else delete next[label];
    setCodes((m) => ({ ...m, [product]: next })); // optimistic
    try {
      await setProductPackaging(product, assign[product] || [], modeOf(product), eans[product], next);
      toast.success(t("productPackaging.codeSaved"));
    } catch (e) {
      setCodes((m) => ({ ...m, [product]: prev }));
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  return (
    <Layout actions={
      <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-packaging-label-button">
        <Plus className="mr-1.5 h-4 w-4" /> {t("productPackaging.addLabel")}
      </Button>
    }>
      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Boxes className="h-6 w-6 text-[hsl(var(--brand))]" /> {t("productPackaging.title")}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("productPackaging.subtitle")}</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : labels.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-12 text-center">
          <Boxes className="h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">{t("productPackaging.noLabels")}</p>
          <Button size="sm" className="mt-4" onClick={() => setAddOpen(true)} data-testid="add-packaging-label-empty">
            <Plus className="mr-1.5 h-4 w-4" /> {t("productPackaging.addLabel")}
          </Button>
        </CardContent></Card>
      ) : productTypes.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t("productPackaging.noProducts")}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {productTypes.map((product) => {
            const selected = assign[product] || [];
            const allMode = selected.length === 0;
            const mode = modeOf(product);
            return (
              <Card key={product} data-testid={`product-packaging-${product}`}>
                <CardContent className="p-4">
                  <div className="mb-2.5 flex items-center justify-between gap-2">
                    <span className="font-medium">{td(product)}</span>
                    {allMode
                      ? <Badge className="badge-warn" data-testid={`pkg-allmode-${product}`}>{t("productPackaging.allAvailable")}</Badge>
                      : <Badge className="badge-ok">{t("productPackaging.nSelected", { n: selected.length })}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {labels.map((p) => {
                      const on = selected.includes(p.label);
                      return (
                        <Button
                          key={p.label}
                          type="button"
                          size="sm"
                          variant={on ? "default" : "outline"}
                          className="h-7 px-2.5 text-xs font-mono"
                          onClick={() => toggle(product, p.label)}
                          data-testid={`pkg-toggle-${product}-${p.label}`}
                        >
                          {on && <Check className="mr-1 h-3 w-3" />}
                          {p.label}
                        </Button>
                      );
                    })}
                  </div>

                  {(() => {
                    const eanLabels = selected.length ? labels.filter((p) => selected.includes(p.label)) : labels;
                    if (eanLabels.length === 0) return null;
                    return (
                      <div className="mt-3 border-t border-border pt-3">
                        <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("productPackaging.eanCodeTitle")}</p>
                        <div className="grid grid-cols-1 gap-1.5">
                          {eanLabels.map((p) => (
                            <div key={p.label} className="flex items-center gap-2">
                              <span className="w-16 shrink-0 truncate font-mono text-[11px] text-muted-foreground" title={p.label}>{p.label}</span>
                              <Input
                                defaultValue={(eans[product] || {})[p.label] || ""}
                                onBlur={(e) => saveEan(product, p.label, e.target.value)}
                                placeholder={t("productPackaging.eanPlaceholder")}
                                inputMode="numeric"
                                className="h-7 flex-1 font-mono text-xs"
                                data-testid={`ean-input-${product}-${p.label}`}
                              />
                              <Input
                                defaultValue={(codes[product] || {})[p.label] || ""}
                                onBlur={(e) => saveCode(product, p.label, e.target.value)}
                                placeholder={t("productPackaging.codePlaceholder")}
                                className="h-7 w-24 shrink-0 font-mono text-xs"
                                data-testid={`code-input-${product}-${p.label}`}
                              />
                            </div>
                          ))}
                        </div>
                        <p className="mt-1.5 text-[11px] text-muted-foreground">{t("productPackaging.eanCodeHint")}</p>
                      </div>
                    );
                  })()}

                  <div className="mt-3 border-t border-border pt-3">
                    <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">{t("productPackaging.stockMode")}</p>
                    <div className="inline-flex rounded-lg border border-border p-0.5" role="group">
                      <Button
                        type="button" size="sm" variant={mode === "bulk_kg" ? "default" : "ghost"}
                        className="h-7 gap-1.5 px-2.5 text-xs"
                        onClick={() => setMode(product, "bulk_kg")}
                        data-testid={`stock-mode-bulk-${product}`}
                      >
                        <Scale className="h-3.5 w-3.5" /> {t("productPackaging.modeBulk")}
                      </Button>
                      <Button
                        type="button" size="sm" variant={mode === "package" ? "default" : "ghost"}
                        className="h-7 gap-1.5 px-2.5 text-xs"
                        onClick={() => setMode(product, "package")}
                        data-testid={`stock-mode-package-${product}`}
                      >
                        <Package className="h-3.5 w-3.5" /> {t("productPackaging.modePackage")}
                      </Button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      {mode === "bulk_kg" ? t("productPackaging.modeBulkHint") : t("productPackaging.modePackageHint")}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {addOpen && <AddLabelDialog onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); load(); }} />}
    </Layout>
  );
}

function AddLabelDialog({ onClose, onAdded }) {
  const { t } = useT();
  const [label, setLabel] = useState("");
  const [net, setNet] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const lbl = label.trim();
    if (!lbl) { toast.error(t("productPackaging.labelRequired")); return; }
    const netVal = net === "" ? null : Number(net);
    if (netVal !== null && (Number.isNaN(netVal) || netVal <= 0)) { toast.error(t("settings.netGt0")); return; }
    setSaving(true);
    try {
      await createPackagingOption({ label: lbl, ...(netVal !== null ? { net_kg: netVal } : {}) });
      toast.success(t("settings.packAdded"));
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedAdd")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("productPackaging.addLabel")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("productPackaging.labelName")}</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("productPackaging.labelPlaceholder")} data-testid="new-label-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("productPackaging.netKg")}</Label>
            <Input type="number" value={net} onChange={(e) => setNet(e.target.value)} placeholder="0.4" data-testid="new-net-input" />
            <p className="text-[11px] text-muted-foreground">{t("productPackaging.netHint")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-label-save">{t("common.add")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
